import { StarFilled } from '@ant-design/icons';
import { Card, Flex, Rate, Typography } from 'antd';
import { useEffect, useState } from 'react';
import ReactGa from 'react-ga4';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { rateInterview } from '@/api/interviews';
import RateForm from '@/components/form/RateForm';
import {
  CLIENT,
  DASHBOARD,
  HOME,
  PRIVACY_POLICY,
  RECRUITER,
} from '@/constants/routes';
import { useApiMutation } from '@/hooks/api';
import { useInterview } from '@/hooks/queries/useInterview';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useUrlState } from '@/hooks/useUrlState';
import Logo from '@/icons/logo-black.svg?react';
import { queryKeys } from '@/shared/lib/queryKeys';
import Loader from '@/UI/Loader';
import { SectionHeading, SubsectionHeading } from '@/UI/typography';

import type { RateFormValues } from '@/components/form/RateForm';

type RateSubmitPayload = {
  comments: string;
  rating: number;
  [key: string]: string | boolean | number;
};

const RateInterview = () => {
  const [rating, setRating] = useState<number>(0);
  const { history } = useUrlState();
  const { isClient, isRecruiter } = useCurrentUser();
  const params = useParams() as { interviewId: string };
  const interviewId = params.interviewId;
  const { data: interview = null, isLoading: loading } =
    useInterview(interviewId);
  const [t] = useTranslation();

  const rateMutation = useApiMutation({
    mutationFn: (data: RateSubmitPayload) => rateInterview(interviewId, data),
    invalidateKeys: [queryKeys.interviews.detail(interviewId)],
    onSuccess: () => {
      history.push(DASHBOARD);
    },
  });

  useEffect(() => {
    ReactGa.send('pageview');
  }, []);

  const onFinish = (data: RateFormValues) => {
    rateMutation.mutate({ ...data, rating });
  };

  return (
    <Flex
      align="center"
      justify="center"
      flex={1}
      className="rate-interview-page"
    >
      <Loader spinning={loading}>
        {interview ? (
          <Flex vertical flex={1}>
            <Flex justify="center">
              <Logo />
            </Flex>
            <Card>
              <Link
                to={
                  isClient
                    ? RECRUITER + '/' + interview?.recruiter.id
                    : CLIENT + '/' + interview?.client.id
                }
              >
                <Flex justify="center">
                  <SectionHeading>
                    {t('HOW_ENJOY')}{' '}
                    {isRecruiter
                      ? interview?.client.name
                      : interview?.recruiter.name}{' '}
                    ?
                  </SectionHeading>
                </Flex>
              </Link>
              <Typography.Text>{t('LEAVE_FEEDBACK')}</Typography.Text>
              <Flex justify="center">
                <SubsectionHeading>{t('OVERALL_RATING')}</SubsectionHeading>
              </Flex>
              <Flex justify="center">
                <Rate
                  value={rating}
                  character={<StarFilled />}
                  onChange={(value) => setRating(value)}
                />
              </Flex>
              <Flex justify="center">
                <SubsectionHeading>{t('SPECIFY_ANSWER')}</SubsectionHeading>
              </Flex>
              <RateForm onFinish={onFinish} loading={rateMutation.isPending} />
            </Card>
            <Flex justify="center" align="center" gap={12}>
              <Link to={HOME} target="_blank" rel="noopener noreferrer">
                {t('COPYRIGHT_HIREPORT', { year: new Date().getFullYear() })}
              </Link>
              <Link
                to={PRIVACY_POLICY}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('PRIVACY_POLICY')}
              </Link>
            </Flex>
          </Flex>
        ) : (
          ''
        )}
      </Loader>
    </Flex>
  );
};

export default RateInterview;
